import { Box } from "@otrium/core";
import { useCallback, useMemo, useState } from "react";
import { useLingui } from "@lingui/react";
import {
  FilterContentWrapper,
  ScrollWrapper,
} from "src/organisms/ProductList/components/Filters/DesktopFilter.styled";
import { Button } from "src/atoms/Button";
import { InputTextSearch } from "../InputTextSearch";
import { Radio } from "src/atoms/Radio";
import { Checkbox } from "src/atoms/Checkbox";
import { ColorFilterCheckbox } from "../ColorFilterCheckbox";
import {
  MobileClearAllButton,
  MobileFilterContainer,
} from "./MobileFilter.styled";
import { LabelNavigation } from "../LabelNavigation";
import { ArrowLeft } from "src/organisms/MobileFilters/DrawerFilter.styled";
import { MessageDescriptor } from "@lingui/core";
import classNames from "classnames";
import { Trans } from "@lingui/macro";
import { theme } from "src/theme";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { useGroupedSizes } from "src/molecules/Filter/components/Sizes/hooks/useGroupedSizes";
import { GroupedSizesView } from "src/molecules/Filter/components/Sizes/GroupedSizesView";
import { shopTypeTranslation } from "src/translations/shopTypeTranslation";
import { useFacetContext } from "src/organisms/ProductList/components/Facets/facet.context";
import { useListingState } from "src/hooks/stores/use-listing-store";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import type {
  ListingFacet,
  ListingFacetData,
} from "src/types/adapters/listing.type";
import {
  isListingFacetBrandData,
  isListingFacetCategoryData,
  isListingFacetColorData,
} from "src/utils/type-guards";

type MobileFilterProps = {
  title: MessageDescriptor;
  facet: ListingFacet;
  isOpen: boolean;
  toggleOpen: () => void;
} & (
  | { showSearch: true; searchPlaceholder: string }
  | { showSearch?: false; searchPlaceholder?: string }
);

const multicolorIconLink =
  "https://colors-icons.s3.eu-central-1.amazonaws.com/multicoloured.png";

const SINGLE_SELECT_KEYS = new Set(["discount"]);
const COLOR_KEYS = new Set(["colors"]);

const GENDER_ORDER: Record<string, number> = {
  [ShopType.Women]: 0,
  [ShopType.Men]: 1,
  [ShopType.Kids]: 2,
};

function compareByLabel(a: ListingFacetData, b: ListingFacetData) {
  const aLabel = (a.label.split("||")[1] || a.label).toLocaleLowerCase();
  const bLabel = (b.label.split("||")[1] || b.label).toLocaleLowerCase();

  return aLabel.localeCompare(bLabel);
}

function compareByGenderOrder(a: ListingFacetData, b: ListingFacetData) {
  const aOrder = GENDER_ORDER[a.value.toLocaleLowerCase()] ?? Infinity;
  const bOrder = GENDER_ORDER[b.value.toLocaleLowerCase()] ?? Infinity;

  return aOrder - bOrder;
}

function MobileFilter({
  facet,
  title,
  searchPlaceholder,
  showSearch,
  isOpen,
  toggleOpen,
}: MobileFilterProps): JSX.Element {
  const { i18n } = useLingui();
  const [searchQuery, setSearchQuery] = useState("");
  const featureFlags = useFeatureFlags();
  const { refine, clearKey } = useFacetContext();
  const { totalResults: total } = useListingState();

  const isColor = COLOR_KEYS.has(facet.key);
  const isSingleSelect = SINGLE_SELECT_KEYS.has(facet.key);
  const isMultiColumn = facet.key !== "brands" && facet.key !== "discount";

  const facetData = useMemo<ListingFacetData[]>(
    () => (facet.type === "range" ? [] : facet.data),
    [facet.type, facet.data]
  );

  const items = useMemo<ListingFacetData[]>(() => {
    const sorter =
      facet.key === "genders" ? compareByGenderOrder : compareByLabel;

    const filtered = searchQuery
      ? facetData.filter((option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : facetData;

    return [...filtered].sort(sorter);
  }, [facet.key, facetData, searchQuery]);

  const selectedCount = useMemo(
    () => facetData.filter((option) => option.selected).length,
    [facetData]
  );

  const hasFilters = selectedCount > 0;

  const handleRefine = useCallback(
    (value: string) => {
      refine(facet.key, value);
    },
    [facet.key, refine]
  );

  const handleClear = useCallback(() => {
    clearKey(facet.key);
  }, [facet.key, clearKey]);

  const groupedSizes = useGroupedSizes(facet.type === "size" ? facet.data : []);
  const useGroupedSizesView =
    facet.key === "size" && featureFlags.sizingFiltersWithSchema;

  return (
    <MobileFilterContainer className={classNames({ ["open"]: isOpen })}>
      <LabelNavigation
        label={title}
        labelSuffix={selectedCount > 0 ? ` (${selectedCount})` : undefined}
        leftAdornment={
          <MobileClearAllButton className="no-padding" onClick={toggleOpen}>
            <ArrowLeft strokeWidth="2" />
          </MobileClearAllButton>
        }
        rightAdornment={
          hasFilters ? (
            <MobileClearAllButton onClick={handleClear}>
              <Trans>Clear all</Trans>
            </MobileClearAllButton>
          ) : undefined
        }
      />
      <div className="content-wrapper">
        {showSearch ? (
          <Box paddingBlockEnd={3}>
            <InputTextSearch
              placeholder={searchPlaceholder}
              handleChange={(event) =>
                setSearchQuery(event.currentTarget.value)
              }
              onClearSearch={() => setSearchQuery("")}
            />
          </Box>
        ) : null}

        <ScrollWrapper>
          {useGroupedSizesView ? (
            <GroupedSizesView
              mappedSizes={groupedSizes.mappedSizes}
              onSelect={handleRefine}
            />
          ) : (
            <FilterContentWrapper
              as={isColor ? "div" : "ul"}
              isMultiColumn={isMultiColumn && items.length > 1}
              count={Math.round(items.length / 2)}
            >
              {items.map((option) => {
                const splitLabel = option.label.split("||");
                const id = splitLabel.length > 1 ? splitLabel[0] : option.value;
                const label =
                  splitLabel.length > 1 ? splitLabel[1] : option.label;
                const genderDescriptor =
                  facet.key === "genders"
                    ? shopTypeTranslation[
                        String(option.value).toLocaleLowerCase()
                      ]
                    : undefined;
                const localizedLabel =
                  facet.key === "genders" && genderDescriptor
                    ? i18n._(genderDescriptor)
                    : label;

                if (isColor && isListingFacetColorData(option)) {
                  return (
                    <ColorFilterCheckbox
                      key={id}
                      id={id}
                      checked={option.selected}
                      onChange={() => handleRefine(option.value)}
                      icon={
                        option.icon ||
                        (option.hex ? undefined : multicolorIconLink)
                      }
                      name={localizedLabel}
                      hex={option.hex}
                    />
                  );
                }

                if (isSingleSelect) {
                  return (
                    <Box
                      as="li"
                      key={`${facet.key}-${id}`}
                      mb={theme.space.space24}
                    >
                      <Radio
                        id={id}
                        label={localizedLabel}
                        name={facet.key}
                        value={option.value}
                        checked={option.selected}
                        onChange={() => handleRefine(option.value)}
                      />
                    </Box>
                  );
                }

                return (
                  <Box as="li" key={`${facet.key}-${id}`}>
                    <Checkbox
                      id={
                        isListingFacetBrandData(option) ||
                        isListingFacetCategoryData(option)
                          ? option.id
                          : undefined
                      }
                      checked={option.selected}
                      onChange={() => handleRefine(option.value)}
                    >
                      {localizedLabel}
                    </Checkbox>
                  </Box>
                );
              })}
            </FilterContentWrapper>
          )}
        </ScrollWrapper>

        <Button
          fullWidth
          variant="primary"
          onClick={toggleOpen}
          disabled={total <= 0}
          className="total-button"
        >
          <span>
            {total > 0 ? (
              <Trans>Show {total} results</Trans>
            ) : (
              <Trans>Close</Trans>
            )}
          </span>
        </Button>
      </div>
    </MobileFilterContainer>
  );
}

export { MobileFilter };
